/** Minimal profile data embedded on renderer rows for immediate author display. */
export function buildProfileSnapshot(profile: Record<string, unknown> | null | undefined): Record<string, unknown> | null {
  if (!profile) return null;
  return {
    id: profile.id,
    workspace_id: profile.workspace_id == null ? null : String(profile.workspace_id),
    full_name: profile.full_name,
    flexicon: profile.flexicon,
    role: profile.role,
    tag: profile.tag,
    tag_color: profile.tag_color,
  };
}

/** Minimal emoji data embedded on renderer rows for reactions and Richdown entities. */
export function buildEmojiSnapshot(emoji: Record<string, unknown> | null | undefined): Record<string, unknown> | null {
  if (!emoji) return null;
  return {
    id: emoji.id,
    shortcode: emoji.shortcode,
    public_url: emoji.public_url,
    native: emoji.native,
  };
}

/** Minimal channel data embedded on renderer rows for Richdown channel entities. */
export function buildChannelSnapshot(channel: Record<string, unknown> | null | undefined): Record<string, unknown> | null {
  if (!channel) return null;
  return {
    id: channel.id,
    name: channel.name,
    kind: channel.kind,
    parent_id: channel.parent_id,
    flexicon: channel.flexicon,
  };
}

export interface EntityTargets {
  readonly channel: string[];
  readonly emoji: string[];
  readonly profile: string[];
  readonly spammer: string[];
}

/** Extracts unique Richdown entity ids without requiring a DOM or React runtime. */
export function extractEntityTargets(html: unknown): EntityTargets {
  if (typeof html !== 'string' || !html.includes('<entity')) return emptyEntityTargets();

  const channel = new Set<string>();
  const emoji = new Set<string>();
  const profile = new Set<string>();
  const spammer = new Set<string>();
  const entityTagRegex = /<entity\b[^>]*>/gi;

  for (const match of html.matchAll(entityTagRegex)) {
    const tag = match[0];
    const type = extractAttribute(tag, 'data-type')?.toLowerCase();
    if (type === 'channel' || type === 'profile' || type === 'spammer') {
      const target = extractAttribute(tag, 'data-target');
      if (target) ({ channel, profile, spammer })[type].add(target);
    } else if (type === 'emoji') {
      const id = extractAttribute(tag, 'data-id');
      if (id) emoji.add(id);
    }
  }

  return {
    channel: [...channel],
    emoji: [...emoji],
    profile: [...profile],
    spammer: [...spammer],
  };
}

/**
 * Extracts profile/channel ids referenced by a typed row's `data` payload
 * (announcement actors like `profile_id`/`inviter_profile_id`, breakout channel
 * refs). Values are hashids, matching the Dexie entity table keys.
 */
export function entityTargetsFromMessageData(data: unknown): EntityTargets {
  if (typeof data !== 'object' || data === null) return emptyEntityTargets();

  const channel = new Set<string>();
  const profile = new Set<string>();
  for (const [key, value] of Object.entries(data)) {
    if (typeof value !== 'string' || value.length === 0) continue;
    if (key.endsWith('profile_id')) profile.add(value);
    else if (key.endsWith('channel_id')) channel.add(value);
  }

  return { channel: [...channel], emoji: [], profile: [...profile], spammer: [] };
}

function extractAttribute(tag: string, name: string): string | undefined {
  const pattern = new RegExp(`${name}=["']([^"']+)["']`, 'i');
  return pattern.exec(tag)?.[1];
}

function emptyEntityTargets(): EntityTargets {
  return { channel: [], emoji: [], profile: [], spammer: [] };
}
